// Revenue estimation helpers.
//
// Methodology:
//  - RPM (revenue per 1000 views) is derived from each niche's
//    monetization_potential score (1-10). Industry-typical mapping:
//      score 1  → $1.50 RPM  (low-monetization niches)
//      score 5  → $7.50 RPM  (mid)
//      score 10 → $15 RPM    (top: finance, B2B, tech)
//  - "Lifetime estimate" = views × RPM / 1000
//  - "Monthly run-rate"   = lifetime estimate × (30 / days_since_publish)
//  - Channels under 1,000 subs are not yet eligible for YouTube Partner
//    Program — we surface the figure but label it as "potential" earnings.
//
// These are *rough* estimates. Actual creator earnings depend on country,
// CPM, audience demographics, sponsor deals, and Shorts vs long-form.

const YPP_SUB_THRESHOLD = 1000;

export function estimateRPM(monetizationScore = 5) {
  const score = Math.max(1, Math.min(10, monetizationScore));
  return Math.round(score * 1.5 * 100) / 100;
}

export function estimateVideoLifetimeRevenue(views, monetizationScore) {
  const rpm = estimateRPM(monetizationScore);
  return Math.round((views / 1000) * rpm);
}

export function estimateMonthlyRunRate(video, monetizationScore) {
  const lifetime = estimateVideoLifetimeRevenue(video.views, monetizationScore);
  const published = new Date(video.published_at);
  const days = Math.max(1, (Date.now() - published.getTime()) / 86_400_000);
  return Math.round(lifetime * (30 / days));
}

export function isYPPEligible(subs) {
  return subs >= YPP_SUB_THRESHOLD;
}

export function estimateNicheMonthly(niche) {
  if (!niche?.videos?.length) return 0;
  const score = niche.scores?.monetization_potential ?? 5;
  return niche.videos.reduce(
    (sum, v) => sum + estimateMonthlyRunRate(v, score),
    0,
  );
}

export function estimateNicheBreakdown(niche) {
  if (!niche?.videos?.length) {
    return { monetized: 0, preYpp: 0, total: 0, monetizedCount: 0, totalCount: 0 };
  }
  const score = niche.scores?.monetization_potential ?? 5;
  let monetized = 0;
  let preYpp = 0;
  let monetizedCount = 0;
  niche.videos.forEach((v) => {
    const m = estimateMonthlyRunRate(v, score);
    if (isYPPEligible(v.subscribers)) {
      monetized += m;
      monetizedCount += 1;
    } else {
      preYpp += m;
    }
  });
  return {
    monetized,
    preYpp,
    total: monetized + preYpp,
    monetizedCount,
    totalCount: niche.videos.length,
  };
}

export function fmtMoney(n) {
  if (n == null) return "—";
  if (n >= 1_000_000) return "$" + (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return "$" + (n / 1_000).toFixed(1) + "K";
  return "$" + Math.round(n).toLocaleString();
}
